import { CheckIcon, CircleDotIcon, PauseCircleIcon } from "lucide-react";

import { type ChartConfig } from "@/components/ui/chart";

import { ProjectRoleType, ProjectStatus } from "@/features/projects/types";
import { Status } from "@/features/tickets/types";

export const PROJECT_STATUS_STYLES: Record<ProjectStatus, string> = {
  Active: "text-primary",
  "On hold": "text-amber-600 dark:text-amber-400",
  Complete: "text-muted-foreground",
};

export const PROJECT_ROLE_STYLES: Record<ProjectRoleType, string> = {
  Admin: "text-foreground",
  Developer: "text-foreground",
  Viewer: "text-muted-foreground",
};

export const PROJECT_STATUS_CONFIG: Record<ProjectStatus, { icon: React.ElementType; className: string; description: string }> = {
  Active: {
    icon: CircleDotIcon,
    className: "text-primary",
    description: "Project is live and actively being worked on.",
  },
  "On hold": {
    icon: PauseCircleIcon,
    className: "text-amber-600 dark:text-amber-400",
    description: "Work is temporarily paused.",
  },
  Complete: {
    icon: CheckIcon,
    className: "text-muted-foreground",
    description: "Project has been finished.",
  },
};

export const PROJECT_STATUS_TO_API: Record<ProjectStatus, string> = {
  Active: "Active",
  "On hold": "OnHold",
  Complete: "Complete",
};

export const PROJECT_STATUS_FROM_API: Record<string, ProjectStatus> = {
  Active: "Active",
  OnHold: "On hold",
  Complete: "Complete",
};

// ─── Overview charts ───────────────────────────────────────────────────────────

/**
 * Ticket statuses keyed for chart use. ChartContainer turns each config key into
 * a `--color-<key>` CSS variable, so keys must be free of whitespace.
 */
export const OVERVIEW_STATUS_KEYS: Record<Status, string> = {
  Open: "open",
  "In Progress": "inProgress",
  "In Review": "inReview",
  Resolved: "resolved",
  Closed: "closed",
};

export const OVERVIEW_STATUS_COLOURS: Record<Status, string> = {
  Open: "var(--chart-2)",
  "In Progress": "var(--chart-3)",
  "In Review": "var(--chart-1)",
  Resolved: "var(--chart-4)",
  Closed: "var(--chart-5)",
};

export const OVERVIEW_TICKET_TREND_CONFIG = {
  created: { label: "Created", color: "var(--chart-2)" },
  resolved: { label: "Resolved", color: "var(--chart-4)" },
} satisfies ChartConfig;

export const OVERVIEW_STATUS_CONFIG = {
  value: { label: "Tickets" },
  open: { label: "Open", color: "var(--chart-2)" },
  inProgress: { label: "In Progress", color: "var(--chart-3)" },
  inReview: { label: "In Review", color: "var(--chart-1)" },
  resolved: { label: "Resolved", color: "var(--chart-4)" },
  closed: { label: "Closed", color: "var(--chart-5)" },
} satisfies ChartConfig;

export const OVERVIEW_PRIORITY_CONFIG = {
  count: { label: "Tickets" },
  Critical: { label: "Critical", color: "var(--chart-5)" },
  High: { label: "High", color: "var(--chart-4)" },
  Medium: { label: "Medium", color: "var(--chart-3)" },
  Low: { label: "Low", color: "var(--chart-1)" },
} satisfies ChartConfig;

export const OVERVIEW_MEMBER_ACTIVITY_CONFIG = {
  closed: { label: "Tickets closed", color: "var(--chart-3)" },
} satisfies ChartConfig;
